import { useState } from "react"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { formatDuration, formatFileSize } from "./formatters"

export default function RecordingsModal({ isOpen, onClose, loading, data, error, courseId, meetingId }) {
  const [ showPasswordFor, setShowPasswordFor ] = useState(null)
  const [ copied, setCopied ] = useState(false)
  const [ transcript, setTranscript ] = useState({ visible: false, loading: false, parsed: null, error: null, topic: null, downloadUrl: null })

  function togglePassword(id) {
    setShowPasswordFor(prev => prev === id ? null : id)
  }

  async function openTranscript(recording) {
    setTranscript({ visible: true, loading: true, parsed: null, error: null, topic: recording.topic, downloadUrl: recording.transcript_url })
    try {
      const res = await fetch(recording.transcriptProxyUrl)
      const json = await res.json()
      if (json.error) throw new Error(json.error)
      setTranscript(prev => ({ ...prev, loading: false, parsed: json.parsed || [] }))
    } catch (e) {
      setTranscript(prev => ({ ...prev, loading: false, error: e.message || "Failed to load transcript" }))
    }
  }

  function backToRecordings() {
    setTranscript({ visible: false, loading: false, parsed: null, error: null, topic: null, downloadUrl: null })
  }

  function handleClose() {
    backToRecordings()
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) handleClose() }}>
      <Dialog.Content size="xl">
        <Dialog.Header>
          <Dialog.Title>
            {transcript.visible ? `Transcript — ${transcript.topic || "Recording"}` : "Recordings"}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {transcript.visible ? (
            transcript.loading ? (
              <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading transcript...</div>
            ) : transcript.error ? (
              <div className="py-4 text-center text-sm text-webex-red-700 dark:text-webex-red-400">{transcript.error}</div>
            ) : (transcript.parsed || []).length === 0 ? (
              <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">Transcript is empty.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto space-y-4">
                {transcript.parsed.map((entry, i) => (
                  <div key={i}>
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-webex-gray-1000 dark:text-white">{entry.speaker || "Unknown"}</span>
                      {entry.timestamp && (
                        <span className="text-xs text-webex-gray-500 dark:text-webex-gray-400">{entry.timestamp}</span>
                      )}
                    </div>
                    <p className="text-sm text-webex-gray-800 dark:text-webex-gray-200 leading-relaxed">{entry.text}</p>
                  </div>
                ))}
              </div>
            )
          ) : (
            loading ? (
              <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading recordings...</div>
            ) : error ? (
              <div className="py-4 text-center text-sm text-webex-red-700 dark:text-webex-red-400">{error}</div>
            ) : (data || []).length === 0 ? (
              <div className="py-8 text-center text-sm text-webex-gray-600 dark:text-webex-gray-400">No recordings available.</div>
            ) : (
              <div className="divide-y divide-webex-gray-200 dark:divide-webex-gray-800">
                {data.map((r, i) => (
                  <div key={i} className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-webex-gray-1000 dark:text-white">{r.topic || "Recording"}</p>
                        <div className="flex items-center gap-3 text-xs text-webex-gray-600 dark:text-webex-gray-400 mt-0.5">
                          {r.format && <span>{r.format.toUpperCase()}</span>}
                          {r.duration_seconds != null && <span>{formatDuration(r.duration_seconds)}</span>}
                          {r.size_bytes && <span>{formatFileSize(r.size_bytes)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        {r.password && (
                          <Button
                            variant="text"
                            size="default"
                            onClick={() => togglePassword(r.id)}
                          >
                            {showPasswordFor === r.id ? "Hide password" : "Show password"}
                          </Button>
                        )}
                        {r.play_url && (
                          <Button
                            variant="secondary"
                            size="default"
                            render={<a href={r.play_url} target="_blank" rel="noreferrer" />}
                          >
                            Play
                          </Button>
                        )}
                        {r.download_url && (
                          <Button
                            variant="primary"
                            size="default"
                            render={<a href={r.download_url} target="_blank" rel="noreferrer" />}
                          >
                            Download
                          </Button>
                        )}
                        {r.transcript_url ? (
                          <Button
                            variant="secondary"
                            size="default"
                            onClick={() => openTranscript({ ...r, transcriptProxyUrl: `/lti/courses/${courseId}/course_meetings/${meetingId}/transcript` })}
                          >
                            Transcript
                          </Button>
                        ) : (
                          <Button variant="secondary" size="default" disabled>
                            Transcript
                          </Button>
                        )}
                      </div>
                    </div>
                    {showPasswordFor === r.id && r.password && (
                      <div className="mt-2 flex items-center gap-2 bg-webex-gray-50 dark:bg-webex-gray-900 rounded-md px-3 py-2">
                        <span className="text-xs text-webex-gray-600 dark:text-webex-gray-400">Password:</span>
                        <code className="text-xs font-mono text-webex-gray-1000 dark:text-white">{r.password}</code>
                        <Button
                          variant="text"
                          size="default"
                          onClick={() => {
                            navigator.clipboard.writeText(r.password)
                            setCopied(true)
                            setTimeout(() => setCopied(false), 1500)
                          }}
                        >
                          {copied ? "Copied!" : "Copy"}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </Dialog.Body>
        <Dialog.Footer>
          {transcript.visible ? (
            <div className="flex items-center gap-2 w-full">
              <Button variant="secondary" onClick={backToRecordings}>
                Back to recordings
              </Button>
              <div className="flex-1" />
              {transcript.downloadUrl && (
                <Button
                  variant="primary"
                  render={<a href={transcript.downloadUrl} target="_blank" rel="noreferrer" />}
                >
                  Download transcript
                </Button>
              )}
            </div>
          ) : (
            <Button variant="secondary" onClick={handleClose}>Close</Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
